"use client"

import { motion, useScroll, useTransform, type MotionValue } from "framer-motion"
import { BellRing, Blocks, CreditCard, Gem, Globe2, Link2, LockKeyhole, Repeat2, ShieldAlert, ShieldCheck, Smartphone, WalletCards, Zap, type LucideIcon } from "lucide-react"
import { type ReactNode, useRef } from "react"

import { cn } from "@/lib/utils"

export type WalletStep = { label: string; title: string; description: string }
export type SecurityFeature = { title: string; description: string; status?: string }
export type DialNode = { label: string; value?: string }

type SectionBaseProps = { eyebrow?: string; title: string; description?: string; className?: string }

const orbitIcons: LucideIcon[] = [WalletCards, Repeat2, Zap, Link2, Gem, Blocks, CreditCard, BellRing, ShieldCheck]

export function CryptoWalletHeroDriftIsland({ children, className }: { children: ReactNode; className?: string }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end start"] })
  const y = useTransform(scrollYProgress, [0, 1], [0, 260])
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.92])
  const opacity = useTransform(scrollYProgress, [0, 0.78, 1], [1, 0.9, 0.36])

  return (
    <div ref={containerRef} className={cn("relative z-0", className)}>
      <motion.div data-crypto-hero-drift style={{ y, scale, opacity }} className="relative will-change-transform">
        {children}
      </motion.div>
    </div>
  )
}

function PhoneScreen({ step, index, progress }: { step: WalletStep; index: number; progress: MotionValue<number> }) {
  const start = index / 3
  const middle = (index + 0.45) / 3
  const end = (index + 0.95) / 3
  const opacity = useTransform(progress, [Math.max(0, start - 0.04), middle, Math.min(1, end + 0.04)], [0, 1, 0])
  const y = useTransform(progress, [Math.max(0, start - 0.08), middle, Math.min(1, end + 0.08)], [150, 0, -150])
  const scale = useTransform(progress, [Math.max(0, start - 0.08), middle, Math.min(1, end + 0.08)], [0.84, 1, 0.88])
  const icons = [Smartphone, Globe2, ShieldCheck]
  const Icon = icons[index % icons.length]

  return (
    <motion.div style={{ opacity, y, scale }} className="absolute inset-0 mx-auto flex max-w-md items-center justify-center">
      <div className="relative w-full overflow-hidden rounded-[2.25rem] border border-border/70 bg-card p-3 shadow-[0_44px_140px_rgba(0,0,0,0.32)]">
        <div aria-hidden="true" className="absolute inset-x-12 top-0 h-28 rounded-full bg-primary/22 blur-3xl" />
        <div className="relative rounded-[1.65rem] border border-border/70 bg-background p-4 shadow-[inset_0_0_0_999px_hsl(var(--background)/0.94)]">
          <div className="mb-5 flex items-center justify-between gap-3">
            <span className="flex size-11 items-center justify-center rounded-card bg-primary/10 text-primary"><Icon className="size-5" /></span>
            <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs text-emerald-600">{step.label}</span>
          </div>
          <p className="font-heading text-3xl font-light leading-tight text-foreground">{step.title}</p>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">{step.description}</p>
          <div className="mt-8 grid gap-3">
            {["Primary wallet", "Chain routes", "Safe approval"].map((item, itemIndex) => (
              <div key={item} className={cn("flex items-center justify-between rounded-card border border-border/70 bg-muted/46 p-3", itemIndex === index && "border-primary/30 bg-primary/10")}>
                <span className="text-sm text-muted-foreground">{item}</span>
                <span className="size-2.5 rounded-full bg-primary" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

function StepCopy({ step, index, progress }: { step: WalletStep; index: number; progress: MotionValue<number> }) {
  const start = index / 3
  const middle = (index + 0.45) / 3
  const end = (index + 0.95) / 3
  const opacity = useTransform(progress, [Math.max(0, start - 0.1), middle, Math.min(1, end + 0.1)], [0.22, 1, 0.22])
  const x = useTransform(progress, [Math.max(0, start - 0.1), middle, Math.min(1, end + 0.1)], [-20, 0, 20])

  return (
    <motion.article style={{ opacity, x }} className="rounded-card border border-border/70 bg-card/80 p-5 shadow-sm backdrop-blur">
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-primary">{step.label}</p>
      <h3 className="mt-3 font-heading text-3xl font-light leading-tight text-foreground">{step.title}</h3>
      <p className="mt-3 text-sm leading-6 text-muted-foreground">{step.description}</p>
    </motion.article>
  )
}

function OrbitNodeTile({ node, index, progress, counterRotate }: { node: DialNode; index: number; progress: MotionValue<number>; counterRotate: MotionValue<number> }) {
  const entrance = index * 0.022
  const opacity = useTransform(progress, [0, 0.015 + entrance, 0.09 + entrance, 1], [0.35, 0.35, 1, 1])
  const scale = useTransform(progress, [0, 0.015 + entrance, 0.09 + entrance, 1], [0.86, 0.86, 1, 1])
  const Icon = orbitIcons[index % orbitIcons.length]

  return (
    <motion.div style={{ opacity, scale, rotate: counterRotate }} className="relative z-20 flex size-20 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-[1.1rem] border border-border/70 bg-card p-2.5 text-center text-foreground shadow-[0_0_0_10px_hsl(var(--background)/0.82),0_18px_54px_rgba(0,0,0,0.16)] backdrop-blur-xl lg:size-[5.5rem]">
      <span className="flex size-8 items-center justify-center rounded-xl bg-primary/10 text-primary lg:size-9">
        <Icon className="size-4" />
      </span>
      <span className="mt-2 max-w-full truncate text-xs font-medium leading-none">{node.label}</span>
      {node.value && <span className="mt-1 max-w-full truncate text-[0.62rem] uppercase tracking-[0.12em] text-muted-foreground">{node.value}</span>}
    </motion.div>
  )
}

export function CryptoWalletSetupScrollIsland({ eyebrow, title, description, steps, className }: SectionBaseProps & { steps: WalletStep[] }) {
  const sectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] })
  const progressScale = useTransform(scrollYProgress, [0, 1], [0.08, 1])

  return (
    <section ref={sectionRef} className={cn("relative h-[390vh] bg-muted/42", className)}>
      <div className="sticky top-0 h-screen overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(circle_at_52%_18%,hsl(var(--primary)/0.16),transparent_26%),linear-gradient(180deg,hsl(var(--background)/0.9),hsl(var(--muted)/0.55))]" />
        <div className="absolute inset-0 mx-auto grid w-full max-w-theme items-center gap-10 px-4 py-10 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
          <div className="relative z-10">
            {eyebrow && <p className="mb-5 text-sm font-medium uppercase tracking-[0.22em] text-primary">{eyebrow}</p>}
            <h2 className="font-heading text-5xl font-light leading-tight text-foreground sm:text-6xl">{title}</h2>
            {description && <p className="mt-5 max-w-xl text-lg leading-8 text-muted-foreground">{description}</p>}
            <div className="mt-8 h-1 w-52 overflow-hidden rounded-full bg-border"><motion.div style={{ scaleX: progressScale }} className="h-full origin-left rounded-full bg-primary" /></div>
            <div className="mt-8 hidden gap-3 lg:grid">
              {steps.slice(0, 3).map((step, index) => <StepCopy key={step.title} step={step} index={index} progress={scrollYProgress} />)}
            </div>
          </div>
          <div className="relative z-10 hidden h-[42rem] lg:block">
            {steps.slice(0, 3).map((step, index) => <PhoneScreen key={step.title} step={step} index={index} progress={scrollYProgress} />)}
          </div>
          <div className="relative z-10 grid gap-4 lg:hidden">
            {steps.map((step, index) => <StepCopy key={step.title} step={step} index={index} progress={scrollYProgress} />)}
          </div>
        </div>
      </div>
    </section>
  )
}

export function CryptoWalletDialScrollIsland({ eyebrow, title, description, nodes, className }: SectionBaseProps & { nodes: DialNode[] }) {
  const sectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] })
  const rotate = useTransform(scrollYProgress, [0, 1], [18, -36])
  const counterRotate = useTransform(scrollYProgress, [0, 1], [-18, 36])
  const copyY = useTransform(scrollYProgress, [0, 1], [34, -34])
  const copyOpacity = useTransform(scrollYProgress, [0, 0.18, 0.82, 1], [0.58, 1, 1, 0.62])
  const dialY = useTransform(scrollYProgress, [0, 1], ["-50%", "-50%"])
  const dialNodes = nodes.slice(0, 9)

  return (
    <section ref={sectionRef} className={cn("relative h-[250vh] bg-background text-foreground", className)}>
      <div className="sticky top-0 h-screen overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(circle_at_88%_50%,hsl(var(--primary)/0.18),transparent_32%),linear-gradient(180deg,hsl(var(--background)),hsl(var(--muted)/0.44))]" />
        <div className="absolute inset-0 mx-auto grid w-full max-w-theme items-center gap-10 px-4 py-10 sm:px-6 lg:grid-cols-[0.82fr_1.18fr] lg:px-8">
          <motion.div style={{ y: copyY, opacity: copyOpacity }} className="relative z-10 max-w-3xl">
            {eyebrow && <p className="mb-5 text-sm font-medium uppercase tracking-[0.22em] text-primary">{eyebrow}</p>}
            <h2 className="font-heading text-5xl font-light leading-tight text-foreground sm:text-7xl">{title}</h2>
            {description && <p className="mt-5 max-w-2xl text-lg leading-8 text-muted-foreground">{description}</p>}
            <div className="mt-8 flex max-w-xl flex-wrap gap-2 lg:hidden">
              {dialNodes.slice(0, 6).map((node) => (
                <span key={node.label} className="rounded-full border border-border/70 bg-card/80 px-3 py-2 text-xs text-muted-foreground shadow-sm backdrop-blur">
                  {node.label}
                </span>
              ))}
            </div>
          </motion.div>
          <div className="relative z-10 min-h-[32rem] lg:min-h-[40rem]">
            <motion.div data-crypto-dial style={{ rotate, y: dialY }} className="absolute right-[-23rem] top-1/2 size-[44rem] rounded-full border border-foreground/14 bg-background/42 shadow-[0_44px_160px_rgba(0,0,0,0.12)] backdrop-blur md:right-[-27rem] md:size-[56rem] lg:right-[-33rem] lg:size-[68rem]">
              <div aria-hidden="true" className="pointer-events-none absolute inset-16 z-0 rounded-full border border-dashed border-foreground/16" />
              <div aria-hidden="true" className="pointer-events-none absolute inset-32 z-0 rounded-full border border-foreground/8" />
              {dialNodes.map((node, index) => {
                const angle = 96 + (168 / Math.max(1, dialNodes.length - 1)) * index
                const radius = 47
                const x = (50 + radius * Math.cos((angle * Math.PI) / 180)).toFixed(3)
                const y = (50 + radius * Math.sin((angle * Math.PI) / 180)).toFixed(3)

                return (
                  <div key={`${node.label}-${index}`} data-crypto-orbit-node className="absolute z-20" style={{ left: `${x}%`, top: `${y}%` }}>
                    <OrbitNodeTile node={node} index={index} progress={scrollYProgress} counterRotate={counterRotate} />
                  </div>
                )
              })}
              <div className="absolute left-1/2 top-1/2 flex size-36 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-border/70 bg-card text-center shadow-[0_24px_90px_rgba(0,0,0,0.16)]">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-primary">route</p>
                  <p className="mt-1 font-heading text-3xl font-light text-foreground">360</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}

export function CryptoSecurityFeatureScrollIsland({ eyebrow, title, description, features, className }: SectionBaseProps & { features: SecurityFeature[] }) {
  const sectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] })
  const titleOpacity = useTransform(scrollYProgress, [0, 0.7, 1], [1, 1, 0.48])

  return (
    <section ref={sectionRef} className={cn("relative h-[230vh] bg-foreground text-background", className)}>
      <div className="sticky top-0 h-screen overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(var(--primary)/0.18),transparent_30%),linear-gradient(180deg,hsl(var(--foreground)),hsl(var(--foreground)))]" />
        <div className="absolute inset-0 mx-auto grid w-full max-w-theme items-center gap-10 px-4 py-10 sm:px-6 lg:px-8">
          <motion.div style={{ opacity: titleOpacity }} className="max-w-4xl">
            {eyebrow && <p className="mb-5 text-sm font-medium uppercase tracking-[0.22em] text-primary">{eyebrow}</p>}
            <h2 className="font-heading text-5xl font-light leading-tight sm:text-7xl">{title}</h2>
            {description && <p className="mt-5 max-w-2xl text-lg leading-8 text-background/68">{description}</p>}
          </motion.div>
          <div className="relative min-h-[22rem]">
            <div aria-hidden="true" className="absolute inset-x-0 top-1/2 h-px bg-background/12" />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {features.map((feature, index) => <SecurityScrollCard key={feature.title} feature={feature} index={index} progress={scrollYProgress} />)}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function SecurityScrollCard({ feature, index, progress }: { feature: SecurityFeature; index: number; progress: MotionValue<number> }) {
  const icons = [WalletCards, LockKeyhole, ShieldAlert, Globe2]
  const Icon = icons[index % icons.length]
  const offset = index * 0.08
  const y = useTransform(progress, [0, 0.18 + offset, 0.52 + offset, 1], [220 + index * 38, 0, -24, -220])
  const opacity = useTransform(progress, [0, 0.14 + offset, 0.72, 1], [0, 1, 1, 0.28])
  const scale = useTransform(progress, [0, 0.22 + offset, 0.82], [0.92, 1, 0.96])

  return (
    <motion.article style={{ y, opacity, scale }} className="min-h-[18rem] rounded-panel border border-background/16 bg-background/10 p-5 shadow-[0_28px_90px_rgba(0,0,0,0.26)] backdrop-blur-xl">
      <div className="mb-12 flex items-center justify-between gap-4"><span className="flex size-11 items-center justify-center rounded-xl bg-background/10 text-primary"><Icon className="size-5" /></span><span className="rounded-full bg-emerald-300/12 px-3 py-1 text-xs text-emerald-100">{feature.status ?? "active"}</span></div>
      <h3 className="text-xl font-medium text-background">{feature.title}</h3>
      <p className="mt-3 text-sm leading-6 text-background/66">{feature.description}</p>
    </motion.article>
  )
}
